import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { useLocation } from "react-router-dom";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { useCookies } from "react-cookie";
import { AmplifyUser } from "@aws-amplify/ui";
import awsConfig from "../aws-config/aws-config.json";

import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { EnvConfig, GetEnv } from "src/utils/EnvConfig";
import { Auth } from "aws-amplify";
import { CognitoUser } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { errorNotification } from "src/utils/alerts";
import {
  PATH_NAMES,
  PUBLIC_ROUTES,
  removeOrgLoginId,
  StorageKeyName,
} from "src/utils/constants";

export type ApolloClientContextData = {
  amplifyUser: AmplifyUser | undefined;
  setAmplifyUser: Dispatch<
    SetStateAction<ApolloClientContextData["amplifyUser"]>
  >;
  logout: () => void;
};
export const ApolloClientContext = createContext<ApolloClientContextData>(
  {} as ApolloClientContextData
);

interface Props {
  uri: string;
  wsUri: string;
  authChildren: React.ReactNode;
  noAuthChildren: React.ReactNode;
  children?: React.ReactNode;
}

const env = GetEnv() as EnvConfig;

const ApolloClientProvider: FC<Props> = ({
  children,
  uri,
  wsUri,
  authChildren,
  noAuthChildren,
}) => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const navigate = useNavigate();
  const location = useLocation();

  const [cognitoUser, setCognitoUser] = useState<CognitoUser | undefined>(
    undefined
  );

  const [amplifyUser, setAmplifyUser] =
    useState<ApolloClientContextData["amplifyUser"]>(undefined);

  const checkCookieCreds = async () => {
    if (window.location.pathname.split("/")[1] === PATH_NAMES.SIGN_NOW) return;
    try {
      let userData = await Auth.currentAuthenticatedUser();
      if (userData) {
        setCognitoUser(userData);
      } else {
        logout();
      }
    } catch (e) {
      logout();
    }
  };

  const getAuthToken = () => {
    let authorization = "";

    if (amplifyUser) {
      return amplifyUser.getSignInUserSession()?.getAccessToken().getJwtToken();
    }

    const userId =
      cookies[
        `CognitoIdentityServiceProvider.${
          awsConfig[env.env].Auth.userPoolWebClientId
        }.LastAuthUser`
      ];
    if (userId) {
      authorization =
        cookies[
          `CognitoIdentityServiceProvider.${
            awsConfig[env.env].Auth.userPoolWebClientId
          }.${userId}.accessToken`
        ];
    }
    return authorization;
  };

  const authLink = setContext((_, { headers }) => {
    let authorization = getAuthToken();
    return {
      headers: {
        ...headers,
        authorization,
      },
    };
  });

  const httpLink = new HttpLink({
    uri,
    credentials: "omit",
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUri,
      connectionParams: {
        Authorization: getAuthToken(),
      },
    })
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );

  const cache = new InMemoryCache({
    addTypename: false,
  }).restore({});

  const client = new ApolloClient({
    cache,
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
      },
    },
    link: ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, extensions }) => {
            errorNotification({
              message: `${message}`,
            });
            console.error(`GraphQL error: ${message}`);
            if (extensions.code === "UNAUTHENTICATED") {
              logout();
            }
          });
        }
        if (networkError) {
          errorNotification({
            message: `${networkError}`,
          });
          console.error(`Network error: ${networkError}`);
        }
      }),
      new RetryLink({ attempts: { max: 3 } }),
      authLink,
      splitLink,
    ]),
  });

  useEffect(() => {
    if (amplifyUser) {
      checkCookieCreds();
    }
  }, [amplifyUser]);

  useEffect(() => {
    try {
      Auth.currentSession().then(
        () => {
          checkCookieCreds();
        },
        () => logout()
      );
    } catch (e) {
      logout();
    }
  }, []);

  // used to ignore /:id patterns while matching paths
  function matchPathname(pattern: string, pathname: string): boolean {
    const regexPattern = pattern.replace(/:[^/]+/g, "([^/]+)");
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(pathname);
  }

  const logout = () => {
    Auth.signOut().then(() => {
      if (env) {
        let userId =
          cookies[
            `CognitoIdentityServiceProvider.${
              awsConfig[env.env].Auth.userPoolWebClientId
            }.LastAuthUser`
          ];
        removeCookie(
          `CognitoIdentityServiceProvider.${
            awsConfig[env.env].Auth.userPoolWebClientId
          }.LastAuthUser`
        );
        if (userId) {
          removeCookie(
            `CognitoIdentityServiceProvider.${
              awsConfig[env.env].Auth.userPoolWebClientId
            }.${userId}.accessToken`
          );
        }
      }
      setAmplifyUser(undefined);
      setCognitoUser(undefined);
      const userInfo = localStorage.getItem("user");

      // check for /<orgId>/login
      const regexPattern = /^\/[0-9A-Fa-f-]{36}\/login\/?$/;
      if (regexPattern.test(location.pathname)) {
        // save locationOfferingId if available
        const urlSearchParams = new URLSearchParams(window.location.search);
        const locationOfferingId = urlSearchParams.get("locationOfferingId");
        const locationId = urlSearchParams.get("locationId");
        if (locationOfferingId) {
          sessionStorage.setItem(
            StorageKeyName.LocationOfferingId,
            locationOfferingId
          );
          locationId &&
            sessionStorage.setItem(
              StorageKeyName.LocationId,
              urlSearchParams.get("locationId")!
            );
        }

        removeOrgLoginId();
        navigate(location.pathname);
        // save orgLoginId for getUser Call
        localStorage.setItem(
          StorageKeyName.OrgLoginId,
          location.pathname.split("/")[1]
        );
      } else if (
        userInfo ||
        location.pathname.includes(`/${PATH_NAMES.SINDRO}/`)
      ) {
        const user = userInfo && JSON.parse(userInfo);
        const path = user
          ? `/${PATH_NAMES.SINDRO}/${user.organizationId}`
          : location.pathname;
        navigate(`${path}`);
        localStorage.clear();
      } else {
        // if its public route then dont navigate to login screen
        for (const pattern of Object.values(PUBLIC_ROUTES)) {
          if (matchPathname(pattern, location.pathname)) return;
        }

        // check for orgLoginID exists then nav to org/login
        const orgLoginId = localStorage.getItem(StorageKeyName.OrgLoginId);
        navigate(orgLoginId ? `/${orgLoginId}/${PATH_NAMES.LOGIN}` : "/");
      }
    });
  };

  return (
    <ApolloClientContext.Provider
      value={{
        amplifyUser,
        logout,
        setAmplifyUser,
      }}
    >
      {cognitoUser ? (
        <ApolloProvider client={client}>{authChildren}</ApolloProvider>
      ) : (
        <ApolloProvider client={client}>{noAuthChildren}</ApolloProvider>
      )}
    </ApolloClientContext.Provider>
  );
};

export default ApolloClientProvider;
